import React, { useEffect, useMemo, useState } from 'react';
import { DefaultPageLayout } from '../components/layouts/DefaultPageLayout';
import { Button } from '../components/ui/button';
import { TextField } from '../components/ui/TextField';
import BunnyLoader from '../components/ui/BunnyLoader';
import { IconWithBackground } from '../components/ui/icon-with-background';
import { FeatherFileText } from '../components/ui/icons';
import { financeApi } from '../services/api';
import './FinancePayslips.css';

const PAYOUT_BULK_BUSY_KEY = '__bulk__';

function maskBankAccount(acct) {
  if (acct == null || acct === '') return '—';
  const s = String(acct);
  if (s.length <= 4) return s;
  return `····${s.slice(-4)}`;
}

const FinancePayslips = () => {
  const now = useMemo(() => new Date(), []);
  const initialMonth = useMemo(() => {
    const y = now.getFullYear();
    const m = String(now.getMonth() + 1).padStart(2, '0');
    return `${y}-${m}`;
  }, [now]);

  const [payslipMonth, setPayslipMonth] = useState(() => initialMonth);
  const [payoutMonth, setPayoutMonth] = useState(() => initialMonth);

  const [resources, setResources] = useState([]);
  const [resourceId, setResourceId] = useState('');
  const [payoutSelectedResourceIds, setPayoutSelectedResourceIds] = useState([]);

  const [loadingResources, setLoadingResources] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [error, setError] = useState('');
  const [setupOpen, setSetupOpen] = useState(false);
  const [setupLoading, setSetupLoading] = useState(false);
  const [customOpen, setCustomOpen] = useState(false);
  const [customLoading, setCustomLoading] = useState(false);
  const [customLopDays, setCustomLopDays] = useState('');

  const [payrollProfileForm, setPayrollProfileForm] = useState({
    employee_id: '',
    designation: '',
    bank_account_number: ''
  });

  const [salaryRevisionForm, setSalaryRevisionForm] = useState({
    effective_from: '',
    gross_monthly_salary: ''
  });

  const [payoutLoading, setPayoutLoading] = useState(false);
  const [payoutData, setPayoutData] = useState(null);
  const [payoutError, setPayoutError] = useState('');
  const [payoutHasLoaded, setPayoutHasLoaded] = useState(false);
  const [bankSettings, setBankSettings] = useState({
    company_banking_id: '',
    company_bank_account_number: ''
  });
  const [bankSettingsLoading, setBankSettingsLoading] = useState(false);
  const [bankSettingsSaving, setBankSettingsSaving] = useState(false);
  const [payoutRowBusy, setPayoutRowBusy] = useState(null);
  const [bankFileLoading, setBankFileLoading] = useState(false);
  const [payoutOverrideDraftById, setPayoutOverrideDraftById] = useState({});
  const [payoutOverrideSavingById, setPayoutOverrideSavingById] = useState({});

  const { payslipYear, payslipMonthNum } = useMemo(() => {
    const [y, m] = payslipMonth.split('-');
    return { payslipYear: Number(y), payslipMonthNum: Number(m) };
  }, [payslipMonth]);

  const { payoutYear, payoutMonthNum } = useMemo(() => {
    const [y, m] = payoutMonth.split('-');
    return { payoutYear: Number(y), payoutMonthNum: Number(m) };
  }, [payoutMonth]);

  const payoutSelectedSet = useMemo(() => new Set(payoutSelectedResourceIds), [payoutSelectedResourceIds]);

  const payoutFilteredRows = useMemo(() => {
    if (!payoutData?.rows?.length) return [];
    return payoutData.rows.filter((r) => payoutSelectedSet.has(r.resource_id));
  }, [payoutData, payoutSelectedSet]);

  const payoutCompletableResourceIds = useMemo(() => {
    return payoutFilteredRows
      .filter((r) => !r.compute_error && r.amount != null)
      .map((r) => r.resource_id);
  }, [payoutFilteredRows]);

  const missingBankInPayoutSelection = useMemo(
    () =>
      payoutFilteredRows.some(
        (r) => r.amount != null && r.amount > 0 && !(r.bank_account_number || '').trim()
      ),
    [payoutFilteredRows]
  );

  useEffect(() => {
    const loadResources = async () => {
      setError('');
      setLoadingResources(true);
      try {
        const data = await financeApi.getResources();
        const list = Array.isArray(data) ? data : data?.resources || [];
        setResources(list);
        if (!resourceId && list.length > 0) setResourceId(list[0].id);
      } catch (e) {
        console.error(e);
        setError(e?.response?.data?.error || 'Failed to load resources');
      } finally {
        setLoadingResources(false);
      }
    };

    loadResources();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  useEffect(() => {
    if (!resources.length) return;
    setPayoutSelectedResourceIds((prev) => {
      const allIds = resources.map((r) => r.id);
      const valid = new Set(allIds);
      if (prev.length === 0) return allIds;
      const kept = prev.filter((id) => valid.has(id));
      return kept.length > 0 ? kept : allIds;
    });
  }, [resources]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setBankSettingsLoading(true);
      try {
        const s = await financeApi.getCompanyBankSettings();
        if (!cancelled) {
          setBankSettings({
            company_banking_id: s.company_banking_id || '',
            company_bank_account_number: s.company_bank_account_number || ''
          });
        }
      } catch (e) {
        console.error(e);
      } finally {
        if (!cancelled) setBankSettingsLoading(false);
      }
    })();
    return () => {
      cancelled = true;
    };
  }, []);

  // Monthly payouts are loaded on-demand (button click) to reduce initial load time.

  useEffect(() => {
    const selected = resources.find((r) => r.id === resourceId);
    if (!selected) return;

    setPayrollProfileForm({
      employee_id: selected.employee_id || '',
      designation: selected.designation || '',
      bank_account_number: selected.bank_account_number || ''
    });

    // Default effective_from to payslip month start (YYYY-MM-01)
    const [y, m] = payslipMonth.split('-');
    const monthStart = `${y}-${m}-01`;
    setSalaryRevisionForm((prev) => ({
      ...prev,
      effective_from: prev.effective_from || monthStart
    }));
  }, [resources, resourceId, payslipMonth]);

  const downloadBlob = (blob, filename) => {
    const url = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = url;
    a.download = filename;
    a.click();
    window.URL.revokeObjectURL(url);
  };

  const handleGenerate = async () => {
    if (!resourceId) return;

    setError('');
    setGenerating(true);
    try {
      const { blob, filename } = await financeApi.generatePayslip({
        resourceId,
        year: payslipYear,
        month: payslipMonthNum
      });
      downloadBlob(
        blob,
        filename ||
          `Payslip-${resourceId}-${payslipYear}-${String(payslipMonthNum).padStart(2, '0')}.pdf`
      );
    } catch (e) {
      console.error(e);
      const msg = e?.response?.data?.error || 'Failed to generate payslip';
      setError(msg);
      if (String(msg).toLowerCase().includes('no salary revision')) {
        setSetupOpen(true);
      }
    } finally {
      setGenerating(false);
    }
  };

  const handleGenerateCustom = async () => {
    if (!resourceId) return;

    setError('');
    setCustomLoading(true);
    try {
      const { blob, filename } = await financeApi.generateCustomPayslip({
        resourceId,
        year: payslipYear,
        month: payslipMonthNum,
        lopDays: Number(customLopDays)
      });
      downloadBlob(
        blob,
        filename ||
          `Payslip-Custom-${resourceId}-${payslipYear}-${String(payslipMonthNum).padStart(2, '0')}.pdf`
      );
      setCustomOpen(false);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || 'Failed to generate custom payslip');
    } finally {
      setCustomLoading(false);
    }
  };

  const refreshPayouts = async () => {
    try {
      setPayoutLoading(true);
      const data = await financeApi.getMonthlyPayouts(payoutYear, payoutMonthNum);
      setPayoutData(data);
      setPayoutError('');
      setPayoutHasLoaded(true);
      setPayoutOverrideDraftById((prev) => {
        const next = { ...prev };
        for (const row of data?.rows || []) {
          if (next[row.resource_id] === undefined) {
            next[row.resource_id] = row.lop_override ?? '';
          }
        }
        return next;
      });
    } catch (e) {
      setPayoutError(e?.response?.data?.error || 'Failed to refresh payouts');
    } finally {
      setPayoutLoading(false);
    }
  };

  useEffect(() => {
    if (!payoutData?.rows?.length) return;
    setPayoutOverrideDraftById((prev) => {
      const next = { ...prev };
      for (const row of payoutData.rows) {
        next[row.resource_id] = row.lop_override ?? '';
      }
      return next;
    });
  }, [payoutData]);

  const savePayoutLopOverride = async (resourceId) => {
    setPayoutError('');
    setPayoutOverrideSavingById((m) => ({ ...m, [resourceId]: true }));
    try {
      const raw = payoutOverrideDraftById[resourceId];
      const lopOverrideDays = raw === '' || raw === null || raw === undefined ? null : Number(raw);
      await financeApi.setPayoutLopOverride({
        year: payoutYear,
        month: payoutMonthNum,
        resourceId,
        lopOverrideDays
      });
      await refreshPayouts();
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || 'Failed to save LOP override');
    } finally {
      setPayoutOverrideSavingById((m) => ({ ...m, [resourceId]: false }));
    }
  };

  const clearPayoutLopOverride = async (resourceId) => {
    setPayoutError('');
    setPayoutOverrideSavingById((m) => ({ ...m, [resourceId]: true }));
    try {
      setPayoutOverrideDraftById((m) => ({ ...m, [resourceId]: '' }));
      await financeApi.setPayoutLopOverride({
        year: payoutYear,
        month: payoutMonthNum,
        resourceId,
        lopOverrideDays: null
      });
      await refreshPayouts();
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || 'Failed to clear LOP override');
    } finally {
      setPayoutOverrideSavingById((m) => ({ ...m, [resourceId]: false }));
    }
  };

  const togglePayoutResource = (id) => {
    setPayoutSelectedResourceIds((prev) =>
      prev.includes(id) ? prev.filter((x) => x !== id) : [...prev, id]
    );
  };

  const selectAllPayoutResources = () => {
    setPayoutSelectedResourceIds(resources.map((r) => r.id));
  };

  const clearPayoutResourceSelection = () => {
    setPayoutSelectedResourceIds([]);
  };

  const saveBankSettings = async () => {
    setPayoutError('');
    setBankSettingsSaving(true);
    try {
      await financeApi.updateCompanyBankSettings({
        company_banking_id: bankSettings.company_banking_id,
        company_bank_account_number: bankSettings.company_bank_account_number
      });
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || 'Failed to save company bank settings');
    } finally {
      setBankSettingsSaving(false);
    }
  };

  const handleMarkPayoutComplete = async (resourceId) => {
    setPayoutError('');
    setPayoutRowBusy(resourceId);
    try {
      await financeApi.markPayoutsComplete({
        year: payoutYear,
        month: payoutMonthNum,
        resourceIds: [resourceId]
      });
      await refreshPayouts();
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || e?.message || 'Failed to mark payout complete');
    } finally {
      setPayoutRowBusy(null);
    }
  };

  const handleMarkPayoutPending = async (resourceId) => {
    setPayoutError('');
    setPayoutRowBusy(resourceId);
    try {
      await financeApi.markPayoutsPending({
        year: payoutYear,
        month: payoutMonthNum,
        resourceIds: [resourceId]
      });
      await refreshPayouts();
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || e?.message || 'Failed to reopen payout');
    } finally {
      setPayoutRowBusy(null);
    }
  };

  const handleMarkAllPayoutsComplete = async () => {
    if (payoutCompletableResourceIds.length === 0) return;
    setPayoutError('');
    setPayoutRowBusy(PAYOUT_BULK_BUSY_KEY);
    try {
      await financeApi.markPayoutsComplete({
        year: payoutYear,
        month: payoutMonthNum,
        resourceIds: payoutCompletableResourceIds
      });
      await refreshPayouts();
    } catch (e) {
      console.error(e);
      setPayoutError(e?.response?.data?.error || e?.message || 'Failed to mark all payouts complete');
    } finally {
      setPayoutRowBusy(null);
    }
  };

  const handleDownloadBankFile = async () => {
    setPayoutError('');
    setBankFileLoading(true);
    try {
      const { blob, filename } = await financeApi.downloadBankPayrollFile(
        payoutYear,
        payoutMonthNum,
        payoutSelectedResourceIds.length > 0 ? payoutSelectedResourceIds : undefined
      );
      downloadBlob(blob, filename);
    } catch (e) {
      console.error(e);
      setPayoutError(e?.message || 'Failed to download bank file');
    } finally {
      setBankFileLoading(false);
    }
  };

  const saveSetup = async () => {
    if (!resourceId) return;

    setError('');
    setSetupLoading(true);
    try {
      await financeApi.upsertPayrollProfile(resourceId, payrollProfileForm);
      await financeApi.createSalaryRevision(resourceId, {
        effective_from: salaryRevisionForm.effective_from,
        gross_monthly_salary: Number(salaryRevisionForm.gross_monthly_salary)
      });

      // Refresh resource list (so forms reflect latest stored values)
      const data = await financeApi.getResources();
      const list = Array.isArray(data) ? data : data?.resources || [];
      setResources(list);

      if (payoutHasLoaded) {
        await refreshPayouts();
      }

      setSetupOpen(false);
    } catch (e) {
      console.error(e);
      setError(e?.response?.data?.error || 'Failed to save salary setup');
    } finally {
      setSetupLoading(false);
    }
  };

  return (
    <DefaultPageLayout>
      <div className="finance-payslips-container">
        <div className="header">
          <div className="header-left">
            <IconWithBackground icon={<FeatherFileText />} />
            <div>
              <h1 className="text-heading-2">Payroll</h1>
              <p className="text-body text-subtext-color">Payslips and monthly payout runs are below.</p>
            </div>
          </div>
        </div>

        {(loadingResources || generating) && (
          <div className="py-6">
            <BunnyLoader
              size="medium"
              text={loadingResources ? 'Loading resources...' : 'Generating payslip...'}
              animation="bounce"
            />
          </div>
        )}

        {error && (
          <div className="inline-alert">
            {error}
          </div>
        )}

        <div className="content-card finance-card-payslips">
          <h2 className="payout-section-title text-heading-3">Payslips</h2>
          <p className="payout-hint">PDF payslips for one person at a time. Pick the person and the pay month.</p>

          <div className="setup-grid full">
            <TextField label="Resource">
              <select
                className="text-field-input"
                value={resourceId}
                onChange={(e) => setResourceId(e.target.value)}
                disabled={loadingResources || generating}
              >
                {resources.map((r) => (
                  <option key={r.id} value={r.id}>
                    {r.display_name || r.name || r.email || r.id}
                  </option>
                ))}
              </select>
            </TextField>
          </div>

          <div className="setup-grid">
            <TextField label="Payslip month">
              <TextField.Input
                type="month"
                value={payslipMonth}
                onChange={(e) => setPayslipMonth(e.target.value)}
                disabled={loadingResources || generating}
              />
            </TextField>
            <div />
          </div>

          <div className="actions-row compact">
            <Button
              variant="secondary"
              onClick={handleGenerate}
              disabled={!resourceId || loadingResources || generating}
            >
              Download payslip (PDF)
            </Button>
            <span className="finance-muted" aria-hidden>
              ·
            </span>
            <button
              type="button"
              className="finance-ghost-btn"
              onClick={() => setCustomOpen((v) => !v)}
              disabled={loadingResources || generating}
            >
              {customOpen ? 'Hide custom LOP' : 'Custom LOP payslip'}
            </button>
            <span className="finance-muted" aria-hidden>
              ·
            </span>
            <button
              type="button"
              className="finance-ghost-btn"
              onClick={() => setSetupOpen((v) => !v)}
              disabled={loadingResources || generating}
            >
              {setupOpen ? 'Hide salary setup' : 'Salary setup'}
            </button>
          </div>

          {setupOpen && (
            <div className="setup-card">
              <div className="setup-header">
                <div>
                  <div className="text-body-bold font-body-bold">Salary Setup</div>
                  <div className="text-body text-subtext-color">
                    Add payroll profile + a salary revision effective on/before the selected month.
                  </div>
                </div>
              </div>

              {setupLoading && (
                <div className="py-2">
                  <BunnyLoader size="small" text="Saving..." animation="bounce" />
                </div>
              )}

              <div className="setup-grid full">
                <TextField label="Employee ID">
                  <TextField.Input
                    value={payrollProfileForm.employee_id}
                    onChange={(e) => setPayrollProfileForm((p) => ({ ...p, employee_id: e.target.value }))}
                    disabled={setupLoading || generating}
                  />
                </TextField>
              </div>

              <div className="setup-grid">
                <TextField label="Designation">
                  <TextField.Input
                    value={payrollProfileForm.designation}
                    onChange={(e) => setPayrollProfileForm((p) => ({ ...p, designation: e.target.value }))}
                    disabled={setupLoading || generating}
                  />
                </TextField>

                <TextField label="Bank Account Number">
                  <TextField.Input
                    value={payrollProfileForm.bank_account_number}
                    onChange={(e) => setPayrollProfileForm((p) => ({ ...p, bank_account_number: e.target.value }))}
                    disabled={setupLoading || generating}
                  />
                </TextField>
              </div>

              <div className="setup-grid">
                <TextField label="Effective From">
                  <TextField.Input
                    type="date"
                    value={salaryRevisionForm.effective_from}
                    onChange={(e) => setSalaryRevisionForm((s) => ({ ...s, effective_from: e.target.value }))}
                    disabled={setupLoading || generating}
                  />
                </TextField>

                <TextField label="Gross Monthly Salary (INR)">
                  <TextField.Input
                    type="number"
                    min="0"
                    step="0.01"
                    value={salaryRevisionForm.gross_monthly_salary}
                    onChange={(e) => setSalaryRevisionForm((s) => ({ ...s, gross_monthly_salary: e.target.value }))}
                    disabled={setupLoading || generating}
                  />
                </TextField>
              </div>

              <div className="actions-row compact">
                <Button variant="neutral-secondary" onClick={saveSetup} disabled={setupLoading || generating}>
                  Save
                </Button>
                <button
                  type="button"
                  className="finance-ghost-btn"
                  onClick={() => setSetupOpen(false)}
                  disabled={setupLoading || generating}
                >
                  Close
                </button>
              </div>
            </div>
          )}

          {customOpen && (
            <div className="setup-card">
              <div className="setup-header">
                <div>
                  <div className="text-body-bold font-body-bold">Custom Payslip</div>
                  <div className="text-body text-subtext-color">
                    Override LOP days for the selected resource & month.
                  </div>
                </div>
              </div>

              {customLoading && (
                <div className="py-2">
                  <BunnyLoader size="small" text="Generating..." animation="bounce" />
                </div>
              )}

              <div className="setup-grid">
                <TextField label="LOP Days (override)">
                  <TextField.Input
                    type="number"
                    min="0"
                    step="1"
                    value={customLopDays}
                    onChange={(e) => setCustomLopDays(e.target.value)}
                    disabled={customLoading || generating}
                  />
                </TextField>
                <div />
              </div>

              <div className="actions-row compact">
                <Button
                  variant="neutral-secondary"
                  onClick={handleGenerateCustom}
                  disabled={customLoading || generating || customLopDays === ''}
                >
                  Download custom payslip
                </Button>
                <button
                  type="button"
                  className="finance-ghost-btn"
                  onClick={() => setCustomOpen(false)}
                  disabled={customLoading || generating}
                >
                  Close
                </button>
              </div>
            </div>
          )}
        </div>

        <div className="content-card finance-card-payouts">
          <div className="finance-payout-card-header">
            <h2 className="payout-section-title text-heading-3">Monthly payouts</h2>
            <p className="payout-hint payout-hint-tight">
              Prorated like standard payslips. Pick month, people, then review the table.
            </p>
          </div>

          {payoutError && <div className="inline-alert">{payoutError}</div>}

          <div className="finance-payout-controls">
            <div className="finance-payout-month-field">
              <TextField label="Month">
                <TextField.Input
                  type="month"
                  value={payoutMonth}
                  onChange={(e) => setPayoutMonth(e.target.value)}
                  disabled={payoutLoading || bankFileLoading}
                />
              </TextField>
              <div className="actions-row compact">
                <Button
                  variant="neutral-secondary"
                  onClick={refreshPayouts}
                  disabled={payoutLoading || bankFileLoading}
                >
                  {payoutHasLoaded ? 'Refresh payouts' : 'Load payouts'}
                </Button>
              </div>
            </div>
            <div className="finance-payout-people">
              <span className="finance-payout-people-label">
                People <span className="finance-payout-count">({payoutSelectedResourceIds.length})</span>
              </span>
              <div className="finance-payout-multiselect">
                <div className="finance-payout-multiselect-toolbar">
                  <button
                    type="button"
                    className="finance-ghost-btn"
                    onClick={selectAllPayoutResources}
                    disabled={!resources.length || payoutLoading}
                  >
                    All
                  </button>
                  <span className="finance-muted" aria-hidden>
                    ·
                  </span>
                  <button
                    type="button"
                    className="finance-ghost-btn"
                    onClick={clearPayoutResourceSelection}
                    disabled={!resources.length || payoutLoading}
                  >
                    None
                  </button>
                </div>
                <div className="finance-payout-multiselect-list" role="group" aria-label="Resources for payout">
                  {resources.map((r) => (
                    <label key={r.id} className="finance-payout-multiselect-row">
                      <input
                        type="checkbox"
                        checked={payoutSelectedSet.has(r.id)}
                        onChange={() => togglePayoutResource(r.id)}
                        disabled={payoutLoading}
                      />
                      <span>{r.display_name || r.email || r.id}</span>
                    </label>
                  ))}
                  {!resources.length && !loadingResources && (
                    <span className="text-body text-subtext-color">No members loaded.</span>
                  )}
                </div>
              </div>
            </div>
          </div>

          <details className="finance-payout-bank-details">
            <summary className="finance-payout-bank-summary">Bank file settings (company account)</summary>
            <div className="finance-payout-bank-body">
              {bankSettingsLoading && (
                <div className="py-2">
                  <BunnyLoader size="small" text="Loading..." animation="bounce" />
                </div>
              )}
              <div className="setup-grid">
                <TextField label="Banking ID (cell A1)">
                  <TextField.Input
                    value={bankSettings.company_banking_id}
                    onChange={(e) =>
                      setBankSettings((s) => ({ ...s, company_banking_id: e.target.value }))
                    }
                    disabled={bankSettingsLoading || bankSettingsSaving}
                    placeholder="e.g. SIMPLY"
                  />
                </TextField>
                <TextField label="Company debit account">
                  <TextField.Input
                    value={bankSettings.company_bank_account_number}
                    onChange={(e) =>
                      setBankSettings((s) => ({ ...s, company_bank_account_number: e.target.value }))
                    }
                    disabled={bankSettingsLoading || bankSettingsSaving}
                  />
                </TextField>
              </div>
              <div className="actions-row compact">
                <Button
                  variant="neutral-secondary"
                  onClick={saveBankSettings}
                  disabled={bankSettingsLoading || bankSettingsSaving}
                >
                  Save
                </Button>
              </div>
            </div>
          </details>

          {((!bankSettings.company_banking_id?.trim() ||
            !bankSettings.company_bank_account_number?.trim()) ||
            (payoutSelectedResourceIds.length === 0 && !payoutLoading && payoutData?.rows?.length > 0) ||
            missingBankInPayoutSelection) && (
            <div className="finance-payout-notes" aria-live="polite">
              {(!bankSettings.company_banking_id?.trim() ||
                !bankSettings.company_bank_account_number?.trim()) && (
                <p className="finance-payout-note">Open bank file settings to export .xlsx.</p>
              )}
              {payoutSelectedResourceIds.length === 0 && !payoutLoading && payoutData?.rows?.length > 0 && (
                <p className="finance-payout-note">Select at least one person to see rows and export.</p>
              )}
              {missingBankInPayoutSelection && (
                <p className="finance-payout-note">
                  Some selected people are missing bank numbers—they are skipped in the file.
                </p>
              )}
            </div>
          )}

          {payoutLoading && (
            <div className="py-4">
              <BunnyLoader size="medium" text="Loading payouts..." animation="bounce" />
            </div>
          )}

          {!payoutLoading && payoutFilteredRows.length > 0 && (
            <div className="finance-payout-table-bar">
              <button
                type="button"
                className="finance-text-link"
                onClick={handleDownloadBankFile}
                disabled={
                  bankFileLoading ||
                  !payoutHasLoaded ||
                  payoutSelectedResourceIds.length === 0 ||
                  !bankSettings.company_banking_id?.trim() ||
                  !bankSettings.company_bank_account_number?.trim()
                }
              >
                Download .xlsx
              </button>
              <span className="finance-muted" aria-hidden>
                ·
              </span>
              <button
                type="button"
                className="finance-text-link"
                onClick={handleMarkAllPayoutsComplete}
                disabled={
                  !payoutHasLoaded ||
                  payoutCompletableResourceIds.length === 0 ||
                  payoutRowBusy === PAYOUT_BULK_BUSY_KEY ||
                  bankFileLoading ||
                  generating ||
                  setupLoading
                }
              >
                Mark all paid ({payoutCompletableResourceIds.length})
              </button>
            </div>
          )}

          {!payoutLoading && payoutFilteredRows.length > 0 && (
            <div className="payout-table-wrap">
              <table className="payout-table">
                <thead>
                  <tr>
                    <th>Resource</th>
                    <th>Bank (masked)</th>
                    <th>Gross (INR)</th>
                    <th>Paid days</th>
                    <th>LOP</th>
                    <th>LOP override</th>
                    <th>Amount (INR)</th>
                    <th>Status</th>
                    <th>Payment</th>
                  </tr>
                </thead>
                <tbody>
                  {payoutFilteredRows.map((row) => {
                    const busy = payoutRowBusy === row.resource_id;
                    const bulkBusy = payoutRowBusy === PAYOUT_BULK_BUSY_KEY;
                    const canAct = !row.compute_error && row.amount != null;
                    const savingOverride = !!payoutOverrideSavingById[row.resource_id];
                    const isClosed = row.status === 'completed';
                    return (
                      <tr key={row.resource_id}>
                        <td>{row.display_name || row.email || row.resource_id}</td>
                        <td>{maskBankAccount(row.bank_account_number)}</td>
                        <td>{row.gross_monthly_salary != null ? Number(row.gross_monthly_salary).toFixed(2) : '—'}</td>
                        <td>{row.days_present != null ? row.days_present : '—'}</td>
                        <td>{row.lop_days != null ? row.lop_days : '—'}</td>
                        <td>
                          <div className="payout-actions-cell">
                            <input
                              className="text-field-input"
                              style={{ width: 92, height: 32 }}
                              type="number"
                              min="0"
                              step="1"
                              value={payoutOverrideDraftById[row.resource_id] ?? ''}
                              onChange={(e) =>
                                setPayoutOverrideDraftById((m) => ({
                                  ...m,
                                  [row.resource_id]: e.target.value
                                }))
                              }
                              disabled={payoutLoading || savingOverride || busy || bulkBusy || isClosed}
                              placeholder="(auto)"
                            />
                            <span className="finance-muted" aria-hidden>
                              ·
                            </span>
                            <button
                              type="button"
                              className="finance-text-link"
                              onClick={() => savePayoutLopOverride(row.resource_id)}
                              disabled={payoutLoading || savingOverride || busy || bulkBusy || isClosed}
                            >
                              {savingOverride ? 'Saving…' : 'Save'}
                            </button>
                            <span className="finance-muted" aria-hidden>
                              ·
                            </span>
                            <button
                              type="button"
                              className="finance-text-link"
                              onClick={() => clearPayoutLopOverride(row.resource_id)}
                              disabled={payoutLoading || savingOverride || busy || bulkBusy || isClosed}
                              title="Clear override and use attendance-based LOP"
                            >
                              Clear
                            </button>
                          </div>
                        </td>
                        <td>{row.amount != null ? Number(row.amount).toFixed(2) : '—'}</td>
                        <td>
                          {row.compute_error ? (
                            <span className="text-body text-subtext-color" title={row.compute_error}>
                              Error
                            </span>
                          ) : (
                            <span
                              className={`status-pill ${row.status === 'completed' ? 'completed' : 'pending'}`}
                            >
                              {row.status === 'completed' ? 'Completed' : 'Pending'}
                            </span>
                          )}
                        </td>
                        <td>
                          <div className="payout-actions-cell">
                            <button
                              type="button"
                              className="finance-text-link"
                              onClick={() => handleMarkPayoutComplete(row.resource_id)}
                              disabled={
                                !canAct ||
                                row.status === 'completed' ||
                                busy ||
                                bulkBusy ||
                                bankFileLoading ||
                                generating
                              }
                            >
                              Mark paid
                            </button>
                            <span className="finance-muted" aria-hidden>
                              ·
                            </span>
                            <button
                              type="button"
                              className="finance-text-link"
                              onClick={() => handleMarkPayoutPending(row.resource_id)}
                              disabled={
                                row.status !== 'completed' || busy || bulkBusy || bankFileLoading || generating
                              }
                            >
                              Reopen
                            </button>
                          </div>
                        </td>
                      </tr>
                    );
                  })}
                </tbody>
              </table>
            </div>
          )}

          {!payoutLoading && (payoutData == null || payoutData?.rows?.length === 0) && (
            <p className="text-body text-subtext-color">
              {!payoutHasLoaded
                ? 'Click “Load payouts” to fetch this month’s payout rows.'
                : 'No payout rows found for this month.'}
            </p>
          )}
        </div>
      </div>
    </DefaultPageLayout>
  );
};

export default FinancePayslips;

